import React, { useState, useEffect } from 'react';
import SoftGrid from '../Shared/SoftGrid/SoftGrid';
import ApiServices from '../Services/ApiServices';
import { toastHelper } from '../../Helpers/toastHelper';
import MiniLoader from '../Shared/MiniLoader';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
    faSync,
    faPlay,
    faStop,
    faHistory,
    faTrash,
    faClock,
    faCalendarAlt,
    faFilter,
    faCheckCircle,
    faTimesCircle,
    faEdit
} from '@fortawesome/free-solid-svg-icons';
import DeleteAlert from '../Shared/DeleteAlert';
import ModalPopUp from '../Shared/ModalPopUp';
import { useNavigate } from 'react-router-dom';

const AdminSubscription = () => {
    const navigate = useNavigate();
    const [subscriptions, setSubscriptions] = useState([]);
    const [plans, setPlans] = useState([]);
    const [loading, setLoading] = useState(true);
    const [updatingAll, setUpdatingAll] = useState(false);

    // Filters
    const [searchTerm, setSearchTerm] = useState('');
    const [stateFilter, setStateFilter] = useState('all');
    const [planFilter, setPlanFilter] = useState('all');

    // Pagination
    const [pageNumber, setPageNumber] = useState(1);
    const [pageSize, setPageSize] = useState(10);
    const [totalPages, setTotalPages] = useState(1);
    const [totalItems, setTotalItems] = useState(0);

    // Dialogs
    const [deleteDialog, setDeleteDialog] = useState({ open: false, sub: null });
    const [delayDialog, setDelayDialog] = useState({ open: false, sub: null });
    const [delayedDays, setDelayedDays] = useState('');

    const fetchPlans = async () => {
        try {
            const response = await ApiServices.get('Lookup/Plans');
            if (response && Array.isArray(response)) {
                setPlans(response);
            }
        } catch (error) {
            
        }
    };

    const fetchSubscriptions = async (page = pageNumber, search = searchTerm, state = stateFilter, plan = planFilter) => {
        setLoading(true);
        try {
            const params = {
                PageSize: pageSize,
                PageNumber: page,
            };

            if (search) params.Word = search;

            if (state === 'pending') {
                params.UserSubscriptionState = 1;
            } else if (state === 'promoted') {
                params.UserSubscriptionState = 2;
            }

            if (plan !== 'all') {
                params.PlanId = plan;
            }

            const response = await ApiServices.get('Subscription/User/getAll', params);
            if (response && response.check && response.data) {
                setSubscriptions(response.data.items || []);
                setTotalPages(response.data.total_pages || 1);
                setTotalItems(response.data.total_records || 0);
            }
        } catch (error) {
            toastHelper.error(error.msg || 'فشل في جلب الاشتراكات');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchPlans();
    }, []);

    useEffect(() => {
        fetchSubscriptions();
    }, [pageNumber, pageSize, stateFilter, planFilter]);

    const handleSearch = (word) => {
        setSearchTerm(word);
        setPageNumber(1);
        fetchSubscriptions(1, word);
    };

    const handleUpdateAll = async () => {
        setUpdatingAll(true);
        try {
            const response = await ApiServices.put('Subscription/User/updateStatus');
            if (response && response.check) {
                toastHelper.success('تم تحديث جميع حالات الاشتراكات بنجاح');
                fetchSubscriptions();
            }
        } catch (error) {
            toastHelper.error('فشل في تحديث الحالات');
        } finally {
            setUpdatingAll(false);
        }
    };

    const handleAction = async (sub, action) => {
        try {
            let endpoint = '';
            let method = 'put';

            switch (action) {
                case 'start':
                    endpoint = `Subscription/User/start/${sub.id}`;
                    break;
                case 'stop':
                    endpoint = `Subscription/User/stop/${sub.id}`;
                    break;
                case 'renew':
                    endpoint = `Subscription/User/renew/${sub.id}`;
                    break;
                case 'delete':
                    endpoint = `Subscription/User/delete/${sub.id}`;
                    method = 'delete';
                    break;
                default:
                    return;
            }

            setLoading(true);
            const response = await ApiServices[method](endpoint);

            if (response && response.check) {
                toastHelper.success('تمت العملية بنجاح');
                fetchSubscriptions();
            } else {
                toastHelper.error(response.message || 'فشل في تنفيذ العملية');
            }
        } catch (error) {
            toastHelper.error('حدث خطأ أثناء تنفيذ العملية');
        } finally {
            setLoading(false);
            setDeleteDialog({ open: false, sub: null });
        }
    };

    const handleDelay = async () => {
        if (!delayedDays || isNaN(delayedDays)) {
            toastHelper.error('يرجى إدخال عدد أيام صحيح');
            return;
        }

        try {
            setLoading(true);
            const response = await ApiServices.put('Subscription/User/delay', {
                user_subscription_id: delayDialog.sub.id,
                delayed_days: parseInt(delayedDays)
            });

            if (response && response.check) {
                toastHelper.success('تم تأخير الاشتراك بنجاح');
                setDelayDialog({ open: false, sub: null });
                setDelayedDays('');
                fetchSubscriptions();
            }
        } catch (error) {
            toastHelper.error('فشل في تأخير الاشتراك');
        } finally {
            setLoading(false);
        }
    };

    const formatDate = (dateString) => {
        if (!dateString) return '-';
        return new Date(dateString).toLocaleDateString('ar-EG', {
            year: 'numeric',
            month: 'short',
            day: 'numeric'
        });
    };

    const getWhatsAppUrl = (phoneNumber) => {
        if (!phoneNumber) return null;

        const normalizedNumber = String(phoneNumber)
            .replace(/[^\d+]/g, '')
            .replace(/^\+/, '');

        if (!normalizedNumber) return null;

        return `https://wa.me/${normalizedNumber}`;
    };

    const columns = [
        { key: 'user_name', label: 'المستخدم', sortable: true },
        {
            key: 'phone_number',
            label: 'رقم الهاتف',
            render: (val) => {
                const whatsAppUrl = getWhatsAppUrl(val);

                if (!whatsAppUrl) {
                    return <span>-</span>;
                }

                return (
                    <a
                        href={whatsAppUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="phone-link"
                        title="فتح واتساب"
                    >
                        {val}
                    </a>
                );
            }
        },
        {
            key: 'plan_name',
            label: 'الباقة',
            render: (val) => <span className="plan-badge">{val}</span>
        },
        {
            key: 'subscription_state',
            label: 'الحالة',
            render: (val) => (
                <span className={`state-chip ${val === 'Promoted' ? 'promoted' : 'pending'}`}>
                    {val === 'Promoted' ? 'مفعل' : 'قيد الانتظار'}
                </span>
            )
        },
        {
            key: 'is_notified',
            label: 'تم الإشعار',
            render: (val) => {
                const isNotified = val === true || val === 1 || val === '1';
                return (
                <span className={`state-chip ${isNotified ? 'promoted' : 'pending'}`}>
                    {isNotified ? 'نعم' : 'لا'}
                </span>
            );
            }
        },
        {
            key: 'subscription_date',
            label: 'تاريخ البدء',
            render: (val) => (
                <div className="date-cell">
                    <FontAwesomeIcon icon={faCalendarAlt} className="date-icon" />
                    <span>{formatDate(val)}</span>
                </div>
            )
        },
        {
            key: 'subscription_date_expiration',
            label: 'تاريخ الانتهاء',
            render: (val) => (
                <div className="date-cell">
                    <FontAwesomeIcon icon={faCalendarAlt} className="date-icon red" />
                    <span>{formatDate(val)}</span>
                </div>
            )
        },
        {
            key: 'days_remaining',
            label: 'الأيام المتبقية',
            render: (val, row) => {
                const percentage = Math.round((val / row.subscription_days) * 100) || 0;
                return (
                    <div className="progress-container">
                        <div className="progress-text">
                            <span>{val} يوم</span>
                            <span>{percentage}%</span>
                        </div>
                        <div className="progress-bar-bg">
                            <div
                                className="progress-bar-fill"
                                style={{ width: `${Math.min(100, percentage)}%` }}
                            ></div>
                        </div>
                    </div>
                );
            }
        }
    ];

    const actions = (row) => [
        {
            label: row.subscription_state === 'Promoted' ? 'إيقاف' : 'بدء',
            icon: <FontAwesomeIcon icon={row.subscription_state === 'Promoted' ? faStop : faPlay} />,
            onClick: (row) => handleAction(row, row.subscription_state === 'Promoted' ? 'stop' : 'start'),
            className: row.subscription_state === 'Promoted' ? 'stop-btn' : 'start-btn'
        },
        {
            label: 'تعديل',
            icon: <FontAwesomeIcon icon={faEdit} />,
            onClick: (row) => navigate(`/admin/subscriptions/edit/${row.id}`, { state: row }),
            className: 'edit-btn'
        },
        {
            label: 'تجديد',
            icon: <FontAwesomeIcon icon={faSync} />,
            onClick: (row) => handleAction(row, 'renew'),
            className: 'renew-btn'
        },
        {
            label: 'تأخير',
            icon: <FontAwesomeIcon icon={faClock} />,
            onClick: (row) => setDelayDialog({ open: true, sub: row }),
            className: 'delay-btn'
        },
        {
            label: 'السجل',
            icon: <FontAwesomeIcon icon={faHistory} />,
            onClick: (row) => navigate(`/admin/subscriptions/history/${row.id}`),
            className: 'history-btn'
        },
        {
            label: 'حذف',
            icon: <FontAwesomeIcon icon={faTrash} />,
            onClick: (row) => setDeleteDialog({ open: true, sub: row }),
            className: 'delete-btn'
        }
    ];

    return (
        <div className="admin-subs-container">
            <div className="page-header">
                <div className="header-info">
                    <h2>اشتراكات المستخدمين</h2>
                    <p>إدارة وتتبع اشتراكات الطلاب في المنصة ({totalItems})</p>
                </div>
                <div className="header-actions">
                    <button
                        className={`update-all-btn ${updatingAll ? 'loading' : ''}`}
                        onClick={handleUpdateAll}
                        disabled={updatingAll}
                    >
                        {updatingAll ? (
                            <MiniLoader size={16} color="#475569" speed="normal" />
                        ) : (
                            <FontAwesomeIcon icon={faSync} />
                        )}
                        <span>تحديث الكل</span>
                    </button>
                    <button className="add-sub-btn" onClick={() => navigate('/admin/subscriptions/add')}>
                        <FontAwesomeIcon icon={faPlay} />
                        <span>إضافة اشتراك</span>
                    </button>
                </div>
            </div>

            <div className="filters-row">
                <div className="filter-group">
                    <label>حالة الاشتراك</label>
                    <select value={stateFilter} onChange={(e) => setStateFilter(e.target.value)}>
                        <option value="all">جميع الحالات</option>
                        <option value="pending">قيد الانتظار</option>
                        <option value="promoted">مفعل</option>
                    </select>
                </div>
                <div className="filter-group">
                    <label>الباقة</label>
                    <select value={planFilter} onChange={(e) => setPlanFilter(e.target.value)}>
                        <option value="all">جميع الباقات</option>
                        {plans.map(plan => (
                            <option key={plan.id} value={plan.id}>{plan.name}</option>
                        ))}
                    </select>
                </div>
                <button className="reset-filters" onClick={() => {
                    setSearchTerm('');
                    setStateFilter('all');
                    setPlanFilter('all');
                    setPageNumber(1);
                }}>
                    إعادة تعيين
                </button>
            </div>

            <SoftGrid
                columns={columns}
                data={subscriptions}
                isLoading={loading}
                isPaginated={true}
                pageNumber={pageNumber}
                pageSize={pageSize}
                totalPages={totalPages}
                onPageChange={setPageNumber}
                onPageSizeChange={setPageSize}
                onSearch={handleSearch}
                searchPlaceholder="ابحث عن اسم المستخدم..."
                actions={actions}
                columnResizable={true}
                isColumnSelectable={true}
            />

            {/* Delay Dialog */}
            <ModalPopUp
                isOpen={delayDialog.open}
                onClose={() => setDelayDialog({ open: false, sub: null })}
                onConfirm={handleDelay}
                title={`تأخير اشتراك: ${delayDialog.sub?.user_name}`}
                loading={loading}
            >
                <div className="delay-form">
                    <p>أدخل عدد الأيام التي تريد إضافتها لتاريخ انتهاء الاشتراك:</p>
                    <div className="form-group">
                        <label>عدد الأيام</label>
                        <input
                            type="number"
                            className="form-control"
                            value={delayedDays}
                            onChange={(e) => setDelayedDays(e.target.value)}
                            placeholder="مثلاً: 7"
                        />
                    </div>
                </div>
            </ModalPopUp>

            {/* Delete Dialog */}
            <DeleteAlert
                isOpen={deleteDialog.open}
                onClose={() => setDeleteDialog({ open: false, sub: null })}
                onConfirm={() => handleAction(deleteDialog.sub, 'delete')}
                itemName={deleteDialog.sub?.user_name}
                loading={loading}
            />

            <style>{`
                .admin-subs-container {
                    padding: 20px;
                    direction: rtl;
                }
                .page-header {
                    display: flex;
                    justify-content: space-between;
                    align-items: center;
                    margin-bottom: 24px;
                }
                .header-info h2 {
                    margin: 0;
                    color: #1e293b;
                    font-size: 1.5rem;
                }
                .header-info p {
                    margin: 4px 0 0;
                    color: #64748b;
                }
                .header-actions {
                    display: flex;
                    gap: 12px;
                }
                .update-all-btn, .add-sub-btn {
                    border: none;
                    padding: 10px 20px;
                    border-radius: 12px;
                    display: flex;
                    align-items: center;
                    gap: 8px;
                    font-weight: 600;
                    cursor: pointer;
                    transition: all 0.2s;
                }
                .update-all-btn {
                    background: #f1f5f9;
                    color: #475569;
                }
                .update-all-btn:hover {
                    background: #e2e8f0;
                }
                .add-sub-btn {
                    background: #6366f1;
                    color: white;
                }
                .add-sub-btn:hover {
                    background: #4f46e5;
                }
                
                .filters-row {
                    display: flex;
                    gap: 20px;
                    margin-bottom: 20px;
                    background: white;
                    padding: 15px;
                    border-radius: 12px;
                    align-items: flex-end;
                    box-shadow: 0 1px 3px rgba(0,0,0,0.1);
                }
                .filter-group {
                    display: flex;
                    flex-direction: column;
                    gap: 6px;
                }
                .filter-group label {
                    font-size: 0.85rem;
                    color: #64748b;
                    font-weight: 600;
                }
                .filter-group select {
                    padding: 8px 12px;
                    border: 1px solid #e2e8f0;
                    border-radius: 8px;
                    min-width: 150px;
                    background: white;
                }
                .reset-filters {
                    background: none;
                    border: none;
                    color: #6366f1;
                    font-weight: 600;
                    cursor: pointer;
                    padding-bottom: 8px;
                }

                .plan-badge {
                    background: #eef2ff;
                    color: #4f46e5;
                    padding: 4px 10px;
                    border-radius: 6px;
                    font-size: 0.9rem;
                    font-weight: 600;
                }
                .phone-link {
                    color: #0f766e;
                    text-decoration: none;
                    font-weight: 600;
                }
                .phone-link:hover {
                    text-decoration: underline;
                }
                .state-chip {
                    padding: 4px 12px;
                    border-radius: 50px;
                    font-size: 0.85rem;
                    font-weight: 600;
                }
                .state-chip.promoted {
                    background: #dcfce7;
                    color: #166534;
                }
                .state-chip.pending {
                    background: #fee2e2;
                    color: #991b1b;
                }
                .date-cell {
                    display: flex;
                    align-items: center;
                    gap: 8px;
                    font-size: 0.9rem;
                }
                .date-icon {
                    color: #10b981;
                }
                .date-icon.red {
                    color: #ef4444;
                }
                
                .progress-container {
                    width: 150px;
                }
                .progress-text {
                    display: flex;
                    justify-content: space-between;
                    font-size: 0.75rem;
                    margin-bottom: 4px;
                    color: #64748b;
                }
                .progress-bar-bg {
                    height: 6px;
                    background: #f1f5f9;
                    border-radius: 10px;
                    overflow: hidden;
                }
                .progress-bar-fill {
                    height: 100%;
                    background: #6366f1;
                    border-radius: 10px;
                }
                
                .delay-form {
                    padding: 10px 0;
                }
                .delay-form p {
                    color: #64748b;
                    margin-bottom: 15px;
                }
                .form-group label {
                    display: block;
                    margin-bottom: 8px;
                    font-weight: 600;
                    color: #334155;
                }
                .form-control {
                    width: 100%;
                    padding: 10px;
                    border: 1px solid #e2e8f0;
                    border-radius: 8px;
                }
            `}</style>
        </div>
    );
};

export default AdminSubscription;
